import {Pipe, PipeTransform} from '@angular/core';

export type NumberScale = { exp: number, symbol: string };
export const NO_SCALE = {exp: 0, symbol: ''};
const numberScales: Array<NumberScale> = [
	{exp: 0, symbol: ''},
	{exp: 3, symbol: 'K'},
	{exp: 6, symbol: 'M'},
	{exp: 9, symbol: 'B'},
	{exp: 12, symbol: 'T'},
	{exp: 15, symbol: 'P'},
	{exp: 18, symbol: 'E'},
	{exp: 21, symbol: 'Z'},
	{exp: 24, symbol: 'Y'}
];

function roundSigFigs(value, precision): number {
// Set precision
	if (value == 0) {
		return 0;
	}

	const decimalPlaces = Math.ceil(Math.log(value < 0 ? -value: value) * Math.LOG10E);
	const power: number = precision - decimalPlaces;
	return Math.round(value * Math.pow(10, power)) * Math.pow(10, -power);
}

/**
 * Formats a number so that it returns 3 most significant figures and scaled to k, M, B, T
 * @param value
 * @returns {string}
 */
export function formatNumber(value: number, precision?: number, scale?: NumberScale): string {

	if (typeof value !== 'number' || isNaN(value) || Number.NEGATIVE_INFINITY === value || Number.POSITIVE_INFINITY === value) {
		return '-';
	}

	// Set precision. We can't use toPrecision for this bit because the rounding behaviour is not consistent on all browsers.
	// Firefox is different from IE and Chrome. :(
	const significantValue = precision ? roundSigFigs(value, precision) : value;

	const numberScale: NumberScale = scale ? scale : calculateNumberScale(Math.abs(significantValue));
	if (!numberScale) {
		return '-';
	}

	if (value === 0) {
		return '0';
	}

	const scaledValue = significantValue / Math.pow(10, numberScale.exp);
	let scaledValueString = (precision ? scaledValue.toPrecision(precision) : scaledValue) + '';

	// Sometime toPrecision will produce scientific-notation e.g. 555.55.toPrecision(2) === '5.6e+2'
	// We fix that with the code below (we just loose the 3 places of significant digits) but that is ok
	if (scaledValueString.indexOf('e+') > -1) {
		scaledValueString = Number(scaledValueString) + '';
	}
	return scaledValueString + numberScale.symbol;
}

export function calculateNumberScale(value: number): NumberScale {
	if (typeof value !== 'number' || isNaN(value) || Number.NEGATIVE_INFINITY === value || Number.POSITIVE_INFINITY === value) {
		return null;
	}

	let numberScale: NumberScale;
	let i = numberScales.length;
	while (i--) {
		numberScale = numberScales[i];
		if (value >= Math.pow(10, numberScale.exp)) {
			break;
		}
	}

	return numberScale;
}

@Pipe({
    name: 'numberScale',
    standalone: false
})
export class NumberScalePipe implements PipeTransform {
	transform(value: number, precision: string): string {
		const precisionValue = parseFloat(precision);

		if (!isNaN(precisionValue)) {
			return formatNumber(value, precisionValue);
		}

		return formatNumber(value);
	}
}